/**
 * Paket A (A7) — per Drag verstellbare Box-Höhe, die im localStorage
 * gemerkt wird (CSS `resize: vertical` auf dem Wrapper, Persistenz hier).
 *
 * localStorage kann fehlen oder gesperrt sein (Safari Private, Quota) → alle
 * Zugriffe in try/catch, Fallback ist die Default-Höhe.
 */
import { useEffect, useRef, useState } from 'react';

export const MIN_STORED_HEIGHT = 240;

export function readStoredHeight(key: string, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) && n >= MIN_STORED_HEIGHT ? Math.round(n) : fallback;
  } catch {
    return fallback;
  }
}

export function writeStoredHeight(key: string, height: number): void {
  try {
    window.localStorage.setItem(key, String(Math.round(height)));
  } catch {
    /* Speicher nicht verfügbar → Höhe gilt nur für diese Sitzung */
  }
}

/**
 * Liefert ein `ref` für den resizable Wrapper und die aktuelle Höhe.
 * Nach jedem Ziehen (pointerup/mouseup irgendwo im Fenster) wird die
 * tatsächliche Höhe des Elements gelesen und gespeichert.
 */
export function useStoredHeight<T extends HTMLElement = HTMLDivElement>(key: string, fallback: number) {
  const ref = useRef<T | null>(null);
  const [height, setHeight] = useState<number>(() => readStoredHeight(key, fallback));

  useEffect(() => {
    const persist = () => {
      const el = ref.current;
      if (!el) return;
      const h = Math.round(el.getBoundingClientRect().height || el.offsetHeight);
      if (h >= MIN_STORED_HEIGHT && h !== height) {
        setHeight(h);
        writeStoredHeight(key, h);
      }
    };
    window.addEventListener('pointerup', persist);
    window.addEventListener('mouseup', persist);
    return () => {
      window.removeEventListener('pointerup', persist);
      window.removeEventListener('mouseup', persist);
    };
  }, [key, height]);

  return { ref, height, setHeight };
}
